import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { applyTheme, readTheme, Theme, useSession } from '../lib/session';
import { LangSwitch } from './LangSwitch';
import { startActivity, track, trackPage } from '../lib/activity';
import { api, needsOnboarding, Workspace } from '../lib/api';
import { t } from '../lib/i18n';
import { IconBan, IconBell, IconCard, IconChannels, IconCompass, IconContact, IconFilter, IconHelm, IconPulse, IconSearch, IconGlobe, IconHook, IconKey, IconLayers, IconLogout, IconMail, IconMoon, IconPlus, IconRoute, IconSettings, IconShield, IconSun, IconUsers, IconWaves } from './icons';
import { Logo } from './ocean';

/** Staff-written text ends with a full stop before the next sentence. */
const sentence = (s: string) => (/[.!?…]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`);

const NAV = [
  { to: '/', label: 'Обзор', icon: IconWaves, end: true },
  { to: '/emails', label: 'Письма', icon: IconMail },
  { to: '/templates', label: 'Шаблоны', icon: IconLayers },
  { to: '/channels', label: 'Каналы', icon: IconChannels },
  { to: '/notifications', label: 'Уведомления', icon: IconBell },
  { to: '/domains', label: 'Домены', icon: IconGlobe },
  { to: '/api-keys', label: 'API-ключи', icon: IconKey },
  { to: '/webhooks', label: 'Вебхуки', icon: IconHook },
  { to: '/detective', label: 'Детектив', icon: IconSearch },
];

/** Build 3 J1 «Данные»: the audience, with suppressions at the end (who must not get mail). */
const AUDIENCE = [
  { to: '/contacts', label: 'Контакты', icon: IconContact },
  { to: '/events', label: 'События', icon: IconPulse },
  { to: '/segments', label: 'Сегменты', icon: IconFilter },
  { to: '/suppressions', label: 'Подавления', icon: IconBan },
];

const TITLES: [RegExp, string][] = [
  [/^\/emails\/.+/, 'Письма · детали'],
  [/^\/emails/, 'Письма'],
  [/^\/domains\/.+/, 'Домены · DNS'],
  [/^\/domains/, 'Домены'],
  [/^\/api-keys/, 'API-ключи'],
  [/^\/webhooks/, 'Вебхуки'],
  [/^\/suppressions/, 'Подавления'],
  [/^\/contacts\/.+/, 'Контакты · карточка'],
  [/^\/contacts/, 'Контакты'],
  [/^\/events/, 'События'],
  [/^\/segments\/.+/, 'Сегменты · редактор'],
  [/^\/segments/, 'Сегменты'],
  [/^\/pilot\/vocabulary/, 'Пилот · словарь'],
  [/^\/pilot\/.+/, 'Пилот · поручение'],
  [/^\/pilot/, 'Пилот'],
  [/^\/journeys\/.+/, 'Сценарии · течение'],
  [/^\/journeys/, 'Сценарии'],
  [/^\/settings/, 'Настройки'],
  [/^\/channels\/policies\/.+/, 'Каналы · политика'],
  [/^\/channels\/policies/, 'Каналы · политики'],
  [/^\/channels\/templates\/.+/, 'Каналы · шаблон'],
  [/^\/channels\/templates/, 'Каналы · шаблоны'],
  [/^\/channels\/costs/, 'Каналы · расходы'],
  [/^\/channels\/settings/, 'Каналы · настройки'],
  [/^\/channels\/sms/, 'Каналы · SMS'],
  [/^\/channels\/whatsapp/, 'Каналы · WhatsApp'],
  [/^\/channels\/telegram-gateway/, 'Каналы · Telegram Gateway'],
  [/^\/channels\/telegram/, 'Каналы · Telegram-бот'],
  [/^\/channels\/web-push/, 'Каналы · Web Push'],
  [/^\/channels\/push/, 'Каналы · Push'],
  [/^\/channels\/.+/, 'Каналы · канал'],
  [/^\/channels/, 'Каналы'],
  [/^\/notifications\/.+/, 'Уведомления · погружение'],
  [/^\/notifications/, 'Уведомления'],
  [/^\/templates\/generate/, 'Шаблоны · Code2Email'],
  [/^\/templates\/.+/, 'Шаблоны · редактор'],
  [/^\/detective/, 'Детектив логов'],
  [/^\/templates/, 'Шаблоны'],
  [/^\/team/, 'Команда'],
  [/^\/security/, 'Безопасность'],
  [/^\/audit/, 'Журнал действий'],
  [/^\/billing\/invoices\/.+/, 'Тариф и оплата · счёт'],
  [/^\/billing/, 'Тариф и оплата'],
  [/^\/$/, 'Обзор'],
];

export function Layout() {
  const { me, logout } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const [theme, setTheme] = useState<Theme>(readTheme());
  useEffect(() => applyTheme(theme), [theme]);
  // Click-stream (docs/admin/SPEC.md §3.2): only while signed in, and never for a support "view as" session.
  useEffect(() => {
    if (me && !me.support) startActivity();
  }, [me]);
  useEffect(() => {
    if (me && !me.support) trackPage(loc.pathname);
  }, [loc.pathname, me]);

  // The Pilot menu item shows which planner answers now (SPEC §8.1); a failure (Pilot disabled) hides the badge.
  const [pilotMode, setPilotMode] = useState<{ mode: 'llm' | 'rules'; model: string | null } | null>(null);
  useEffect(() => {
    if (!me) return;
    api.get<{ planner: { mode: 'llm' | 'rules'; model: string | null } }>('/pilot/status').then((s) => setPilotMode(s.planner)).catch(() => setPilotMode(null));
  }, [me?.workspace.id]);

  const title = t(TITLES.find(([re]) => re.test(loc.pathname))?.[1] ?? '');
  const nextTheme: Theme = theme === 'dark' ? 'light' : 'dark';
  const done = !me || !needsOnboarding(me);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <Logo />
          <div>
            MailOcean
            <small>{t('океан писем')}</small>
          </div>
        </div>
        {!done && (
          <NavLink to="/onboarding" className="nav-link" style={{ background: 'rgba(23,195,207,.16)', color: '#fff' }}>
            <IconCompass /> {t('Продолжить погружение')}
          </NavLink>
        )}
        <div className="nav-section">{t('Отправка')}</div>
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <Icon /> {t(label)}
          </NavLink>
        ))}
        <div className="nav-section">{t('Аудитория')}</div>
        {AUDIENCE.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <Icon /> {t(label)}
          </NavLink>
        ))}
        <div className="nav-section">{t('Маркетинг')}</div>
        <NavLink to="/pilot" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <IconHelm /> {t('Пилот')}
          {pilotMode && <span className={`nav-badge ${pilotMode.mode}`} title={pilotMode.mode === 'llm' ? `AI: ${pilotMode.model}` : t('Правила (без AI)')}>{pilotMode.mode === 'llm' ? 'AI' : t('правила')}</span>}
        </NavLink>
        <NavLink to="/journeys" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <IconRoute /> {t('Сценарии')}
        </NavLink>
        <div className="nav-section">{t('Аккаунт')}</div>
        <NavLink to="/team" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <IconUsers /> {t('Команда')}
        </NavLink>
        <NavLink to="/security" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <IconShield /> {t('Безопасность')}
        </NavLink>
        <NavLink to="/billing" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <IconCard /> {t('Тариф и оплата')}
        </NavLink>
        {(me?.user.role === 'owner' || me?.user.role === 'admin') && (
          <NavLink to="/audit" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <IconCompass /> {t('Журнал')}
          </NavLink>
        )}
        <NavLink to="/settings" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <IconSettings /> {t('Настройки')}
        </NavLink>
        <WorkspaceSwitcher />
      </aside>
      <div className="main">
        <header className="topbar">
          <span className="crumbs">{title}</span>
          <span className="spacer" />
          <LangSwitch />
          <button className="btn btn-ghost icon-btn" title={t('Сменить тему')} aria-label={t('Сменить тему')} onClick={() => {
              track('theme.toggle', { to: nextTheme });
              setTheme(nextTheme);
            }}>
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={async () => {
              await logout();
              nav('/login');
            }}
          >
            <IconLogout size={16} /> {t('Выйти')}
          </button>
        </header>
        {me?.support && (
          <div className="support-banner" role="status">
            <span>
              <b>{t('Режим просмотра поддержки — только чтение.')}</b> {t('{name} из MailOcean смотрит пространство так, как его видит владелец; изменить ничего нельзя. Доступ записан в журнал действий.', { name: me.support.staff_name })}
            </span>
            <button
              className="btn btn-sm"
              onClick={async () => {
                await logout();
                window.location.assign('/admin/');
              }}
            >
              {t('Выйти')}
            </button>
          </div>
        )}
        {me?.workspace.sending && me.workspace.sending.status !== 'active' && (
          <div className={`sending-banner ${me.workspace.sending.status}`} role="status">
            <span>
              {me.workspace.sending.status === 'suspended' ? (
                <b>{t('Отправка заблокирована MailOcean: API не принимает новые письма.')}</b>
              ) : (
                <b>{t('Отправка приостановлена MailOcean: письма принимаются и ждут в очереди.')}</b>
              )}{' '}
              {sentence(me.workspace.sending.public_message ?? t('Напишите в поддержку, чтобы узнать подробности'))} {t('Письма, уже переданные провайдеру, остановить нельзя.')}
            </span>
          </div>
        )}
        {me?.workspace.sending?.status === 'active' && me.workspace.sending.paused_streams.length > 0 && (
          <div className="sending-banner" role="status">
            <span>
              <b>{t('Поток «{streams}» приостановлен MailOcean.', { streams: me.workspace.sending.paused_streams.map((s) => (s === 'marketing' ? t('маркетинг') : t('транзакционные'))).join('», «') })}</b> {t('Письма этого потока ждут в очереди; остальные отправляются как обычно.')}
            </span>
          </div>
        )}
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Sidebar footer: who you are and which ocean you are in; switch or create workspaces. */
function WorkspaceSwitcher() {
  const { me, refresh } = useSession();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<Workspace[]>([]);

  useEffect(() => {
    if (open) api.get<Workspace[]>('/workspaces').then(setList).catch(() => undefined);
  }, [open]);

  async function go(id: string) {
    await api.post('/workspaces/switch', { tenant_id: id });
    setOpen(false);
    const m = await refresh();
    nav(m && needsOnboarding(m) ? '/onboarding' : '/');
  }
  async function create() {
    const name = prompt(t('Название нового рабочего пространства'));
    if (!name?.trim()) return;
    await api.post('/workspaces', { name: name.trim() });
    setOpen(false);
    const m = await refresh();
    nav(m && needsOnboarding(m) ? '/onboarding' : '/');
  }

  return (
    <div className="sidebar-foot" style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}
        title={t('Сменить рабочее пространство')}
      >
        <div className="who">{me?.user.name}</div>
        <div className="ws">{me?.workspace.name} ▾</div>
        <div style={{ opacity: 0.6 }}>{me?.user.email}</div>
      </button>
      {open && (
        <div className="ws-menu" role="menu">
          {list.map((w) => (
            <button key={w.id} role="menuitem" className={`ws-item${w.current ? ' on' : ''}`} onClick={() => !w.current && go(w.id)}>
              <span>{w.name}</span>
              <small>{w.role}</small>
            </button>
          ))}
          <button role="menuitem" className="ws-item" onClick={create}>
            <span><IconPlus size={14} /> {t('Новое пространство')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
